import { useEffect, useState } from "react";
import axios from "axios";
import { Form, Row, Col, Card } from "react-bootstrap";
import { Button } from "semantic-ui-react";
import Swal from "sweetalert2";

import { headers, formatDisplayDateTime, loader } from "../../../global_vars";
import { projectIdFromPath } from "../../../common/projectWorkspace";

const API = "https://ultima.icsgr.com/api/dashboard/appointments/";
const VIEW_PROJECT = "https://ultima.icsgr.com/api/data_management/project/";

function authHeaders() {
  return {
    ...headers,
    Authorization: "Token " + localStorage.getItem("userToken"),
  };
}

export default function Appointments() {
  const projectId = projectIdFromPath();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [title, setTitle] = useState("");
  const [fileName, setFileName] = useState("");
  const [startsAt, setStartsAt] = useState("");

  const load = async () => {
    const currentHeaders = authHeaders();
    const [projectRes, apptRes] = await Promise.all([
      axios.get(`${VIEW_PROJECT}${projectId}/`, { headers: currentHeaders }),
      axios.get(API, { headers: currentHeaders, params: { project_id: projectId } }),
    ]);
    const project = projectRes?.data || {};
    setFileName((prev) => prev || project.filecode || project.title || "");
    setAppointments(apptRes?.data?.appointments || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (e) {
        if (!cancelled) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: e?.response?.data?.error || "Could not load appointments.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // project id comes from the URL for this page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const createAppointment = async (e) => {
    e.preventDefault();
    if (title.trim().length < 2 || !fileName.trim() || !startsAt) return;
    setSaving(true);
    try {
      await axios.post(
        API,
        {
          title: title.trim(),
          file_name: fileName.trim(),
          starts_at: startsAt,
          project_id: projectId,
        },
        { headers: authHeaders() }
      );
      setTitle("");
      setStartsAt("");
      await load();
      Swal.fire({
        icon: "success",
        title: "Appointment added to the calendar",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.error || "Could not save the appointment.",
      });
    } finally {
      setSaving(false);
    }
  };

  const removeAppointment = async (id) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Remove this appointment?",
      showCancelButton: true,
      confirmButtonText: "Remove",
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.post(`${API}${id}/`, {}, { headers: authHeaders() });
      await load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.error || "Could not remove the appointment.",
      });
    }
  };

  const canSave = title.trim().length >= 2 && fileName.trim() && startsAt;

  return (
    <div className="rootContainer">
      <div className="page_header">
        <h2>Appointments</h2>
      </div>
      <hr />
      <div className="contentBody" style={{ margin: "0 20px 24px" }}>
        <Card style={{ marginBottom: 16 }}>
          <Card.Body>
            <Form onSubmit={createAppointment}>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Time *</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={startsAt}
                      onChange={(ev) => setStartsAt(ev.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>File name *</Form.Label>
                    <Form.Control
                      value={fileName}
                      onChange={(ev) => setFileName(ev.target.value)}
                      maxLength={80}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      value={title}
                      onChange={(ev) => setTitle(ev.target.value)}
                      maxLength={120}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button color="green" type="submit" disabled={!canSave || saving}>
                {saving ? "Saving..." : "Add to calendar"}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {loading ? (
          loader()
        ) : appointments.length === 0 ? (
          <div>No appointments yet.</div>
        ) : (
          appointments.map((appt) => (
            <Card key={appt.appointment_id} style={{ marginTop: 12 }}>
              <Card.Body style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{appt.title}</div>
                  <div style={{ color: "#666", fontSize: 14 }}>
                    {formatDisplayDateTime(appt.starts_at)}
                    {appt.file_name ? ` · ${appt.file_name}` : ""}
                    {appt.created_by_username ? ` · ${appt.created_by_username}` : ""}
                  </div>
                </div>
                <Button basic color="red" type="button" onClick={() => removeAppointment(appt.appointment_id)}>
                  Remove
                </Button>
              </Card.Body>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
